import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";
import { GEAR_CATEGORIES } from "../../constants";
import { navigate } from "../../router";
import Badge from "../../components/Badge";
import CalIcon from "../../components/CalIcon";
import Spinner from "../../components/Spinner";
import CategoryEditor from "./CategoryEditor";
import EventForm from "./EventForm";

export default function AdminPanel({ categoryItems, onCategoryUpdate }) {
  const [events,       setEvents]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [filter,       setFilter]       = useState("upcoming");

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("events")
      .select("*, contributions(count)")
      .order("date", { ascending: false });
    setEvents(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  async function deleteEvent(id) {
    if (!window.confirm("¿Eliminar este evento y todos sus aportes?")) return;
    await supabase.from("events").delete().eq("id", id);
    fetchEvents();
  }

  function copyEventLink(id) {
    const url = `${window.location.origin}${window.location.pathname}#/event/${id}`;
    navigator.clipboard.writeText(url);
    alert("¡Link copiado! Compártelo en WhatsApp.");
  }

  function shareWhatsApp(ev) {
    const url = `${window.location.origin}${window.location.pathname}#/event/${ev.id}`;
    const evDate = ev.date ? new Date(ev.date + "T12:00:00") : null;
    const dateStr = evDate
      ? evDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })
      : "";
    const lines = [
      `🎮 *${ev.name}*`,
      dateStr  ? `📅 ${dateStr}` : null,
      ev.location ? `📍 ${ev.location}` : null,
      ev.startgg_link ? `🏆 Start.gg: ${ev.startgg_link}` : null,
      ``,
      `Anota el equipo que vas a traer:`,
      `👉 ${url}`,
    ].filter(l => l !== null).join("\n");
    navigator.clipboard.writeText(lines);
    alert("¡Mensaje copiado! Pégalo en WhatsApp.");
  }

  const now = new Date();

  const upcomingEvents = events
    .filter(ev => {
      const d = ev.date ? new Date(ev.date + "T12:00:00") : null;
      return !d || d >= now;
    })
    .sort((a, b) => new Date(a.date + "T12:00:00") - new Date(b.date + "T12:00:00"));

  const pastEvents = events
    .filter(ev => {
      const d = ev.date ? new Date(ev.date + "T12:00:00") : null;
      return d && d < now;
    })
    .sort((a, b) => new Date(b.date + "T12:00:00") - new Date(a.date + "T12:00:00"));

  const visibleEvents = filter === "upcoming" ? upcomingEvents : pastEvents;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 16px 80px" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 13, letterSpacing: 3, textTransform: "uppercase", color: "#666", marginBottom: 12 }}>
          Categorías de equipo
        </h2>
        {GEAR_CATEGORIES.map(cat => (
          <CategoryEditor
            key={cat.id}
            catId={cat.id}
            label={cat.label}
            icon={cat.icon}
            items={categoryItems[cat.id]}
            onSave={items => onCategoryUpdate(cat.id, items)}
          />
        ))}
      </div>

      <div className="section-header">
        <h2 style={{ fontSize: 13, letterSpacing: 3, textTransform: "uppercase", color: "#666", margin: 0 }}>Eventos</h2>
        <button className="btn-primary" onClick={() => { setEditingEvent(null); setShowForm(true); }}>
          + Nuevo evento
        </button>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: "1px solid #2a2a2a" }}>
        {[
          { key: "upcoming", label: `Próximos (${upcomingEvents.length})` },
          { key: "past",     label: `Pasados (${pastEvents.length})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            style={{
              background: "none",
              border: "none",
              borderBottom: filter === tab.key ? "2px solid #cc2200" : "2px solid transparent",
              color: filter === tab.key ? "#fff" : "#666",
              cursor: "pointer",
              fontSize: 12,
              fontFamily: "var(--font-body)",
              letterSpacing: 1,
              padding: "8px 16px",
              marginBottom: -1,
              transition: "color 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {showForm && (
        <EventForm
          event={editingEvent}
          onSave={() => { setShowForm(false); fetchEvents(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? <Spinner /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleEvents.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#444" }}>
              <p>{filter === "upcoming" ? "No hay eventos próximos." : "No hay eventos pasados."}</p>
            </div>
          )}
          {visibleEvents.map(ev => {
            const evDate = ev.date ? new Date(ev.date + "T12:00:00") : null;
            const isPast = evDate && evDate < now;
            const count  = ev.contributions?.[0]?.count || 0;
            return (
              <div key={ev.id} className="card" style={{ position: "relative", overflow: "hidden" }}>
                {!isPast && (
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "linear-gradient(180deg,#cc2200,#ff6600)" }} />
                )}
                <div className="event-card-inner">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "#fff" }}>{ev.name}</span>
                      {isPast ? <Badge color="dim">Pasado</Badge> : <Badge color="accent">Próximo</Badge>}
                    </div>
                    <div style={{ fontSize: 12, color: "#999", display: "flex", gap: 14, flexWrap: "wrap" }}>
                      {evDate && (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <CalIcon />
                          {evDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                        </span>
                      )}
                      {ev.location && <span>📍 {ev.location}</span>}
                      <span>👥 {count} aportes</span>
                    </div>
                  </div>
                  <div className="event-card-actions">
                    <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => shareWhatsApp(ev)}>
                      💬 WhatsApp
                    </button>
                    <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => copyEventLink(ev.id)}>
                      📋 Copiar link
                    </button>
                    <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => navigate(`#/event/${ev.id}`)}>
                      👁 Ver
                    </button>
                    <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => { setEditingEvent(ev); setShowForm(true); }}>
                      ✏️ Editar
                    </button>
                    <button className="btn-danger" style={{ fontSize: 11, padding: "6px 10px" }} onClick={() => deleteEvent(ev.id)}>
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
